import AddIcon from "@mui/icons-material/AddRounded";
import { TabPanel } from "@mui/lab";
import { Button } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router";
import { createApiResponse } from "@entrilia/shared/api/axiosHelper";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import useTabSearchParams from "@entrilia/shared/hooks/useTabSearchParams";
import { arrayToMap } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../api/adminApi";
import type { DataImportOption } from "../../../api/types/dataImportTypes";
import type { ObjectClassDefinition, ObjectFieldValues, ObjectPermissions } from "../../../api/types/objectTypes";
import useClientContext from "../../../context/useClientContext";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import PageTabs from "../../common/PageTabs";
import TabLabelWithCount from "../../common/TabLabelWithCount";
import type { ExcelExportConfig } from "./StartObjectsExportConfirmationDialog";
import ObjectsList from "./ObjectsList";
import ObjectsListContext from "./ObjectsListContext";

interface Props {
  objectClassDefinition: ObjectClassDefinition;
  objectPermissions: ObjectPermissions;
  dataImportOptions: DataImportOption[];
  objectDetailsPathPattern: string;
  objectsTitle: string;
  showLogos?: boolean;
  allowCreate?: boolean;
  disablePageTitleUpdate?: boolean;
  excelExportConfig?: ExcelExportConfig;
  headerActions?: ReactNode;
  actionsColumn?: GridColDef<ObjectFieldValues>;
  selectionActions?: ReactNode;
  onTabChange?: () => void;
}

const allObjectsFilterName = "All";

const ObjectsListPageContainer = ({
  objectClassDefinition,
  objectPermissions,
  dataImportOptions,
  objectDetailsPathPattern,
  objectsTitle,
  showLogos,
  allowCreate,
  disablePageTitleUpdate,
  excelExportConfig,
  headerActions,
  actionsColumn,
  selectionActions,
  onTabChange,
}: Props) => {
  usePageTitle(objectsTitle, { disabled: disablePageTitleUpdate });

  const { clientCode, hasAnyPermission } = useClientContext();
  const navigate = useNavigate();

  const predefinedFilterNames = useMemo(
    () => [...objectClassDefinition.predefinedFilters.map((f) => f.filterName), allObjectsFilterName],
    [objectClassDefinition.predefinedFilters]
  );

  const { selectedTab, onTabChange: selectTab } = useTabSearchParams(predefinedFilterNames);

  const getFilterCounters = useCallback(() => {
    if (objectClassDefinition.predefinedFilters.some((f) => f.includeInCounters)) {
      return adminApi.countObjectsByType(objectClassDefinition.objectType);
    }

    return Promise.resolve(createApiResponse(undefined));
  }, [objectClassDefinition.objectType, objectClassDefinition.predefinedFilters]);

  const [filterCounters, , { fetch: refetchFilterCounters }] = useFetch(getFilterCounters);

  const countersByFilterName = useMemo(() => {
    if (filterCounters === undefined) {
      return new Map<string, number>();
    }

    return arrayToMap(
      filterCounters.countsByFilter,
      (item) => item.filterName,
      (item) => item.count
    );
  }, [filterCounters]);

  const showTabs = predefinedFilterNames.length > 1;

  const detailsNavigationState = useMemo(() => ({ tab: showTabs ? selectedTab : undefined }), [showTabs, selectedTab]);

  const getObjectDetailsPath = useCallback(
    (id: string) => generatePath(objectDetailsPathPattern, { clientCode, type: objectClassDefinition.objectType, id }),
    [clientCode, objectClassDefinition.objectType, objectDetailsPathPattern]
  );

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const objectsListContextValue = useMemo(
    () => ({
      objectClassDefinition,
      refreshRows: () => setRefreshTrigger((n) => n + 1),
      refreshObjects: () => {
        setRefreshTrigger((n) => n + 1);
        refetchFilterCounters();
      },
    }),
    [objectClassDefinition, refetchFilterCounters]
  );

  const hasEditPermission = hasAnyPermission(objectPermissions.objectDataWritePermissions);

  const handleTabChange = (tab: string) => {
    selectTab(tab);
    onTabChange?.();
  };

  const showCreationButton = !!allowCreate && hasEditPermission;

  const handleCreateObject = () => {
    const pathToNewObject = generatePath(objectDetailsPathPattern, {
      clientCode,
      type: objectClassDefinition.objectType,
      id: "new",
    });

    navigate(pathToNewObject);
  };

  return (
    <ObjectsListContext value={objectsListContextValue}>
      <GeneralPageHeader title={objectsTitle} hideBorderLine={showTabs}>
        {headerActions}

        {showCreationButton && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateObject}>
            Create New
          </Button>
        )}
      </GeneralPageHeader>

      {!showTabs && (
        <ObjectsList
          dataImportOptions={dataImportOptions}
          objectsTitle={objectsTitle}
          showLogos={showLogos}
          getDetailsPath={getObjectDetailsPath}
          detailsNavigationState={detailsNavigationState}
          refreshTrigger={refreshTrigger}
          excelExportConfig={excelExportConfig}
          actionsColumn={actionsColumn}
          selectionActions={selectionActions}
        />
      )}

      {showTabs && (
        <PageTabs
          tabs={predefinedFilterNames}
          labels={predefinedFilterNames.map((filterName) => {
            const count = countersByFilterName.get(filterName);
            return count === undefined ? (
              filterName
            ) : (
              <TabLabelWithCount key={filterName} label={filterName} count={count} hideCounter={count === 0} />
            );
          })}
          value={selectedTab}
          onChange={handleTabChange}
        >
          {predefinedFilterNames.map((filterName) => (
            <TabPanel key={filterName} value={filterName} sx={{ height: "100%", width: "100%", m: 0, p: 0 }}>
              <ObjectsList
                dataImportOptions={dataImportOptions}
                objectsTitle={objectsTitle}
                showLogos={showLogos}
                predefinedFiltersName={filterName === allObjectsFilterName ? undefined : filterName}
                predefinedFilters={
                  objectClassDefinition.predefinedFilters.find((f) => f.filterName === filterName)?.filters
                }
                getDetailsPath={getObjectDetailsPath}
                detailsNavigationState={detailsNavigationState}
                refreshTrigger={refreshTrigger}
                excelExportConfig={excelExportConfig}
                actionsColumn={actionsColumn}
                selectionActions={selectionActions}
              />
            </TabPanel>
          ))}
        </PageTabs>
      )}
    </ObjectsListContext>
  );
};

export default ObjectsListPageContainer;
